import { ServiceLine } from '@/shared/types/serviceLineTypes';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Card, CardContent } from '@/shared/ui/card';
import { Loader2 } from 'lucide-react';
import { useServiceLineUsage } from '@/domains/service-lines/services/account-service-lines';
import { DataUsageBarChart } from '@/shared/components/DataUsageBarChart';

interface ServiceLineChartDialogProps {
    serviceLine: ServiceLine | undefined;
    isOpen: boolean;
    onClose: () => void;
}

export function ServiceLineChartDialog({ serviceLine, isOpen, onClose }: ServiceLineChartDialogProps) {
    // Fetch usage data if not provided or if provided data is empty
    const { data: dataUsages, isLoading } = useServiceLineUsage(
        isOpen ? serviceLine?.serviceLineNumber : undefined,
        undefined
    );

    if (!serviceLine || !dataUsages) return null;

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-7xl max-h-[95vh] flex flex-col gap-4 overflow-hidden p-4">
                <DialogHeader className="shrink-0">
                    <DialogTitle className="flex items-center gap-2">
                        <span className="text-lg font-medium"> Monthly Data Usage Trends for <span className="font-bold">{serviceLine?.serviceLineName}</span></span>
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-6">
                    {/* Loading State */}
                    {isLoading ? (
                        <Card>
                            <CardContent className="flex items-center justify-center h-64">
                                <div className="text-center">
                                    <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                                    <div className="mb-2">Loading usage data...</div>
                                </div>
                            </CardContent>
                        </Card>
                    ) : dataUsages.length > 0 ? (
                        <Card>
                            <CardContent>
                                <DataUsageBarChart dataUsages={dataUsages} />
                            </CardContent>
                        </Card>
                    ) : (
                        <Card>
                            <CardContent className="flex items-center justify-center h-64">
                                <div className="text-center">
                                    <div className="mb-2">No usage data available</div>
                                    <div className="text-sm">
                                        This service line doesn't have any usage data to display.
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
